import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { tr } from '../til';

const card = {
  background: '#fff',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)'
};

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px'
};

const smallBtn = {
  padding: '6px 12px',
  background: '#eef1f7',
  color: '#1a1a2e',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '12px',
  cursor: 'pointer'
};

const GROUPS = [
  { value: 'shahar_tuman', label: tr("Shahar-tuman"), izoh: tr("inspektor va katta inspektorlar") },
  { value: 'apparat', label: tr("Apparat"), izoh: tr("apparat xodimlari") }
];

const CATEGORIES = [
  { value: 'nazorat', label: tr("Nazorat"), bg: '#e6f0ff', color: '#4f8ef7' },
  { value: 'hujjat', label: tr("Hujjat"), bg: '#f0eaff', color: '#7c4ff7' },
  { value: 'yigilish', label: tr("Yig'ilish"), bg: '#fff8e6', color: '#c47f17' },
  { value: 'hisobot', label: tr("Hisobot"), bg: '#e9f9ed', color: '#2d8a2d' },
  { value: 'boshqa', label: tr("Boshqa"), bg: '#f0f0f5', color: '#666' }
];

const PRIORITIES = [
  { value: 'yuqori', label: tr("Yuqori"), bg: '#ffeaea', color: '#c93636' },
  { value: 'orta', label: tr("O'rta"), bg: '#fff8e6', color: '#c47f17' },
  { value: 'past', label: tr("Past"), bg: '#f0f0f5', color: '#666' }
];

const STATUS_ICON = { bajardi: '✅', bajarmadi: '❌', kutilmoqda: '⏳' };

const find = (list, value) => list.find(x => x.value === value);

// Bugungi sanani "YYYY-MM-DD" ko'rinishida (brauzer vaqt mintaqasida).
const bugun = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const Chip = ({ item }) => item ? (
  <span style={{
    background: item.bg, color: item.color,
    padding: '2px 9px', borderRadius: '20px', fontSize: '11px'
  }}>
    {item.label}
  </span>
) : null;

// Vazifa formasi — qo'shishda ham, tahrirlashda ham.
function TaskForm({ form, setForm }) {
  const set = (field, value) => setForm({ ...form, [field]: value });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
      <input
        type="text"
        placeholder={tr("Vazifa nomi * (masalan: Probatsiyachilarni tekshirish)")}
        value={form.title}
        onChange={e => set('title', e.target.value)}
        style={{ ...inputStyle, gridColumn: '1 / -1' }}
      />

      <select value={form.category} onChange={e => set('category', e.target.value)} style={inputStyle}>
        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{tr("Kategoriya: {label}", { label: c.label })}</option>)}
      </select>

      <select value={form.priority} onChange={e => set('priority', e.target.value)} style={inputStyle}>
        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{tr("Muhimligi: {label}", { label: p.label })}</option>)}
      </select>

      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
          <input
            type="checkbox"
            checked={!!form.task_date}
            onChange={e => set('task_date', e.target.checked ? bugun() : '')}
          />
          {tr("Faqat bitta kunga (belgilanmasa — har kuni takrorlanadi)")}
        </label>

        {form.task_date && (
          <input
            type="date"
            value={form.task_date}
            onChange={e => set('task_date', e.target.value)}
            style={inputStyle}
          />
        )}
      </div>
    </div>
  );
}

function DailyTasks() {
  const [tab, setTab] = useState('royxat'); // 'royxat' | 'hisobot'
  const [group, setGroup] = useState('shahar_tuman');

  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ title: '', category: 'nazorat', priority: 'orta', task_date: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editError, setEditError] = useState('');

  const [date, setDate] = useState(bugun());
  const [report, setReport] = useState(null);
  const [ochiq, setOchiq] = useState({}); // qaysi xodimning tafsiloti ochilgan

  const token = localStorage.getItem('token');

  const loadTasks = useCallback(() => {
    axios.get(`${API}/tasks?staff_group=${group}&date=${date}`, { headers: { authorization: token } })
      .then(res => setTasks(res.data));
  }, [token, group, date]);

  const loadReport = useCallback(() => {
    axios.get(`${API}/tasks/report?date=${date}`, { headers: { authorization: token } })
      .then(res => setReport(res.data));
  }, [token, date]);

  useEffect(() => {
    if (tab === 'royxat') loadTasks();
    else loadReport();
  }, [tab, loadTasks, loadReport]);

  const create = async () => {
    setError(''); setSuccess('');
    if (!form.title.trim()) { setError(tr("Vazifa nomini yozing")); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/tasks`, {
        staff_group: group,
        title: form.title,
        category: form.category,
        priority: form.priority,
        task_date: form.task_date || null
      }, { headers: { authorization: token } });

      setSuccess(tr("\"{title}\" qo'shildi.", { title: form.title }));
      setForm({ title: '', category: 'nazorat', priority: 'orta', task_date: '' });
      loadTasks();
    } catch (err) {
      setError(err.response?.data?.error || tr("Vazifa qo'shilmadi."));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    setEditError('');
    if (!editing.title.trim()) { setEditError(tr("Vazifa nomini yozing")); return; }

    try {
      await axios.put(`${API}/tasks/${editing.id}`, {
        staff_group: editing.staff_group,
        title: editing.title,
        category: editing.category,
        priority: editing.priority,
        task_date: editing.task_date || null
      }, { headers: { authorization: token } });

      setSuccess(tr("Vazifa yangilandi."));
      setEditing(null);
      loadTasks();
    } catch (err) {
      setEditError(err.response?.data?.error || tr("Saqlanmadi."));
    }
  };

  const remove = async (task) => {
    setError(''); setSuccess('');
    if (!window.confirm(
      tr("\"{title}\" o'chirilsinmi?\n\nEski hisobotlarda u saqlanib qoladi, faqat bugundan boshlab xodimlarga ko'rinmaydi.", { title: task.title })
    )) return;

    try {
      await axios.delete(`${API}/tasks/${task.id}`, { headers: { authorization: token } });
      setSuccess(tr("\"{title}\" o'chirildi.", { title: task.title }));
      loadTasks();
    } catch (err) {
      setError(err.response?.data?.error || tr("O'chirib bo'lmadi."));
    }
  };

  const tabStyle = (active) => ({
    padding: '8px 18px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    background: active ? '#1a1a2e' : '#fff',
    color: active ? '#fff' : '#666',
    fontSize: '14px',
    cursor: 'pointer'
  });

  // Hisobot bo'yicha umumiy raqamlar
  const jami = report?.xodimlar.reduce((s, x) => s + x.jami, 0) || 0;
  const bajarildi = report?.xodimlar.reduce((s, x) => s + x.bajardi, 0) || 0;
  const bajarilmadi = report?.xodimlar.reduce((s, x) => s + x.bajarmadi, 0) || 0;
  const foiz = jami ? Math.round((bajarildi * 100) / jami) : 0;

  return (
    <div>
      <h2 style={{ marginBottom: '8px', fontSize: '22px' }}>{tr("Kunlik vazifalar")}</h2>
      <p style={{ marginTop: 0, marginBottom: '20px', color: '#888', fontSize: '13px' }}>
        {tr("Ro'yxat ikkita: apparat xodimlari uchun va shahar-tuman xodimlari uchun. Xodim ilovada bajarganiga galochka qo'yadi, bajarmaganiga sabab yozadi — fayl yuklamaydi.")}
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={() => setTab('royxat')} style={tabStyle(tab === 'royxat')}>
          📋 {tr("Vazifalar ro'yxati")}
        </button>
        <button onClick={() => setTab('hisobot')} style={tabStyle(tab === 'hisobot')}>
          📊 {tr("Bajarish holati")}
        </button>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{ ...inputStyle, marginLeft: 'auto' }}
        />
      </div>

      {tab === 'royxat' && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            {GROUPS.map(g => (
              <button
                key={g.value}
                onClick={() => setGroup(g.value)}
                style={{
                  ...tabStyle(group === g.value),
                  flex: 1,
                  textAlign: 'left',
                  padding: '12px 18px'
                }}
              >
                <div style={{ fontWeight: '600' }}>{g.label}</div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>{g.izoh}</div>
              </button>
            ))}
          </div>

          <div style={{ ...card, marginBottom: '24px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>
              {tr("Yangi vazifa — {label}", { label: find(GROUPS, group).label })}
            </h3>

            <TaskForm form={form} setForm={setForm} />

            {error && (
              <div style={{ background: '#ffeaea', color: '#c93636', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', margin: '12px 0' }}>
                {error}
              </div>
            )}
            {success && (
              <div style={{ background: '#e9f9ed', color: '#2d8a2d', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', margin: '12px 0' }}>
                {success}
              </div>
            )}

            <button
              onClick={create}
              disabled={saving}
              style={{
                marginTop: '12px', padding: '10px 24px',
                background: saving ? '#888' : '#1a1a2e', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '14px',
                width: '100%', cursor: saving ? 'default' : 'pointer'
              }}
            >
              {saving ? tr("Qo'shilmoqda...") : tr("Qo'shish")}
            </button>
          </div>

          <div style={card}>
            <h3 style={{ marginTop: 0, marginBottom: '4px', fontSize: '16px' }}>
              {tr("{label} ro'yxati", { label: find(GROUPS, group).label })}
            </h3>
            <p style={{ marginTop: 0, marginBottom: '16px', color: '#999', fontSize: '12px' }}>
              {date === bugun() ? tr("Bugun") : date} {tr("kuni kuchda bo'lgan vazifalar — jami {soni} ta", { soni: tasks.length })}
            </p>

            {tasks.map((t, i) => (
              <div
                key={t.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px', borderRadius: '10px',
                  background: '#fafbfd', marginBottom: '8px',
                  border: '1px solid #f0f0f5'
                }}
              >
                <span style={{ color: '#bbb', fontSize: '13px', width: '20px' }}>{i + 1}.</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{t.title}</div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                    <Chip item={find(CATEGORIES, t.category)} />
                    <Chip item={find(PRIORITIES, t.priority)} />
                    {t.task_date ? (
                      <span style={{ background: '#fff0e6', color: '#c2621a', padding: '2px 9px', borderRadius: '20px', fontSize: '11px' }}>
                        📅 {tr("faqat {slice}", { slice: String(t.task_date).slice(0, 10) })}
                      </span>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: '11px', alignSelf: 'center' }}>
                        ↻ {tr("har kuni")}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => { setEditError(''); setEditing({ ...t, task_date: t.task_date ? String(t.task_date).slice(0, 10) : '' }); }}
                  style={smallBtn}
                >
                  {tr("Tahrirlash")}
                </button>
                <button onClick={() => remove(t)} style={{ ...smallBtn, color: '#c93636' }}>
                  {tr("O'chirish")}
                </button>
              </div>
            ))}

            {tasks.length === 0 && (
              <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: '20px 0 0' }}>
                {tr("Bu ro'yxat bo'sh. Vazifa qo'shmaguningizcha xodimlarda hech narsa ko'rinmaydi.")}
              </p>
            )}
          </div>
        </>
      )}

      {tab === 'hisobot' && report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            {[
              { label: tr("Jami topshiriq"), value: jami, color: '#1a1a2e' },
              { label: tr("Bajarildi"), value: bajarildi, color: '#2d8a2d' },
              { label: tr("Bajarilmadi"), value: bajarilmadi, color: '#c93636' },
              { label: tr("Samaradorlik"), value: `${foiz}%`, color: '#4f8ef7' }
            ].map(c => (
              <div key={c.label} style={{ ...card, padding: '18px' }}>
                <div style={{ color: '#888', fontSize: '13px', marginBottom: '6px' }}>{c.label}</div>
                <div style={{ color: c.color, fontSize: '26px', fontWeight: '600' }}>{c.value}</div>
              </div>
            ))}
          </div>

          <div style={card}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>
              {tr("Xodimlar — {sana}", { sana: report.sana })}
            </h3>

            {report.xodimlar.map(x => {
              const rang = x.foiz === 100 ? '#2d8a2d' : x.foiz >= 50 ? '#c47f17' : '#c93636';
              return (
                <div
                  key={x.user_id}
                  style={{ border: '1px solid #f0f0f5', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>{x.full_name}</div>
                      <div style={{ color: '#999', fontSize: '12px' }}>
                        {[x.badge_number, x.department, find(GROUPS, x.staff_group)?.label].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <span style={{
                      background: '#f6f8fb', color: rang,
                      padding: '4px 12px', borderRadius: '20px',
                      fontSize: '13px', fontWeight: '600'
                    }}>
                      {x.bajardi}/{x.jami}
                    </span>
                    <button
                      onClick={() => setOchiq({ ...ochiq, [x.user_id]: !ochiq[x.user_id] })}
                      style={smallBtn}
                    >
                      {ochiq[x.user_id] ? tr("Yopish") : tr("Batafsil")}
                    </button>
                  </div>

                  <div style={{ height: '6px', background: '#f0f0f5', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${x.foiz}%`, height: '100%', background: rang, transition: 'width .4s' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '14px', marginTop: '6px', fontSize: '11px', color: '#999' }}>
                    <span>✅ {tr("{bajardi} ta bajardi", { bajardi: x.bajardi })}</span>
                    <span>❌ {tr("{bajarmadi} ta bajarmadi", { bajarmadi: x.bajarmadi })}</span>
                    <span>⏳ {tr("{kutilmoqda} ta kutilmoqda", { kutilmoqda: x.kutilmoqda })}</span>
                  </div>

                  {ochiq[x.user_id] && (
                    <div style={{ marginTop: '12px' }}>
                      {x.topshiriqlar.map(t => (
                        <div
                          key={t.task_id}
                          style={{
                            display: 'flex', gap: '10px', padding: '8px 10px',
                            borderRadius: '8px', marginBottom: '5px',
                            background: t.status === 'bajarmadi' ? '#fff6f6' : '#fafbfd'
                          }}
                        >
                          <span>{STATUS_ICON[t.status]}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px' }}>{t.title}</div>
                            {t.reason && (
                              <div style={{ color: '#c93636', fontSize: '12px', marginTop: '2px' }}>
                                {tr("Sabab: {reason}", { reason: t.reason })}
                              </div>
                            )}
                            {t.status === 'bajardi' && t.marked_at && (
                              <div style={{ color: '#aaa', fontSize: '11px', marginTop: '2px' }}>
                                {tr("{toLocaleTimeString} da bajarildi", { toLocaleTimeString: new Date(t.marked_at).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' }) })}
                              </div>
                            )}
                          </div>
                          <Chip item={find(PRIORITIES, t.priority)} />
                        </div>
                      ))}
                      {x.topshiriqlar.length === 0 && (
                        <p style={{ color: '#999', fontSize: '13px', margin: 0 }}>
                          {tr("Bu guruh uchun vazifa qo'shilmagan.")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {report.xodimlar.length === 0 && (
              <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: '20px 0 0' }}>
                {tr("Lavozimi belgilangan xodim yo'q. Foydalanuvchilar sahifasidan har biriga \"Apparat\" yoki \"Shahar-tuman\" lavozimini biriktiring.")}
              </p>
            )}
          </div>
        </>
      )}

      {editing && (
        <div
          onClick={() => setEditing(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 100
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px', padding: '24px',
              width: '100%', maxWidth: '580px', maxHeight: '88vh', overflowY: 'auto'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>{tr("Vazifani tahrirlash")}</h3>

            <TaskForm form={editing} setForm={setEditing} />

            {editError && (
              <div style={{ background: '#ffeaea', color: '#c93636', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', margin: '12px 0' }}>
                {editError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setEditing(null)} style={{ ...smallBtn, padding: '10px 20px', fontSize: '14px' }}>
                {tr("Bekor qilish")}
              </button>
              <button
                onClick={saveEdit}
                style={{
                  padding: '10px 24px', background: '#1a1a2e', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
                }}
              >
                {tr("Saqlash")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DailyTasks;
